import { Drawer } from '@/components';
import {
  useCreateSubProjectFundInfo,
  useSubProjectsForKS,
  useUpdateSubProjectFundInfo,
} from '@/features/project/_hooks/sub-project';
import type { SubProjectFundInfoFormSchema } from '@/features/project/projects/_models/sub-project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { SubProjectFundInfoForm } from './SubProjectFundInfoForm';

export const SubProjectFundInfoFormDrawer: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();
  const { id: projectId } = useId();
  const subProjects = useSubProjectsForKS(projectId);
  const { openMessage } = useMessage();

  const { mutateAsync: createSubProjectFundInfo } =
    useCreateSubProjectFundInfo(projectId);
  const { mutateAsync: updateSubProjectFundInfo } =
    useUpdateSubProjectFundInfo(projectId);

  const isOpen =
    drawerState.open &&
    drawerState.type === 'execution-progress' &&
    drawerState.formType === 'fund-info';

  const isEdit = isOpen && drawerState.editSubProjectId !== undefined;

  const subProject = isEdit
    ? subProjects.data?.find((sp) => sp.id === drawerState.editSubProjectId)
    : undefined;

  const closeDrawer = () => {
    setDrawerState({ open: false });
  };

  const handleBasicInfoSubmit = async (
    values: z.infer<typeof SubProjectFundInfoFormSchema>,
  ) => {
    invariant(isOpen, t('unreachable-code'));

    try {
      const operation = isEdit
        ? updateSubProjectFundInfo({
            ...values,
            subProjectId: drawerState.editSubProjectId ?? '',
          })
        : createSubProjectFundInfo(values);

      await operation;

      await Promise.all([
        openMessage(
          'success',
          isEdit
            ? t('message-update-completed')
            : t('message-create-completed'),
        ),
        (async () => {
          closeDrawer();
        })(),
      ]);
    } catch {
      await openMessage(
        'error',
        isEdit ? t('message-update-failed') : t('message-create-failed'),
      );
    }
  };

  return (
    <Drawer destroyOnClose open={isOpen} onClose={closeDrawer}>
      <SubProjectFundInfoForm
        initialValues={
          subProject
            ? {
                name: subProject.name,
                localFundOwner: subProject.localFundOwner ?? undefined,
                localFundOverview: subProject.localFundOverview ?? undefined,
              }
            : undefined
        }
        onSubmit={handleBasicInfoSubmit}
      />
    </Drawer>
  );
};
